import { runtimeConfig } from "@/runtime-config";
import { Feature, FeatureFlag } from "@/store/features/features";
import {
  disableFeature,
  enableFeature,
  selectGetFeatureValueIfEnabled,
  selectHasFeature,
} from "@/store/features/features-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  ChangeIndicatorBadge,
  convertToDateString,
  FaroSwitch,
  FaroText,
  TextField,
} from "@faro-lotv/flat-ui";
import { TableCell, TableRow } from "@mui/material";
import { useCallback, useState, type JSX } from "react";

type FeatureEditFieldProps = {
  /** The name of the feature */
  name: FeatureFlag;
} & Feature;

/**
 * @returns A row of the Features Management dialog that allows to modify the feature state
 */
export function FeaturesDialogRow({
  name,
  canBeUserEnabled,
  description,
  isPreviewFeature,
  supportValueInUrl,
  createdAt,
  requiresReload = true,
}: FeatureEditFieldProps): JSX.Element {
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useAppSelector(selectHasFeature(name));

  const enabledByDefault = isPreviewFeature
    ? runtimeConfig.enablePreviewFeatures
    : false;
  // If the feature is enabled by default, but also supports values,
  // it cannot be explicitly disabled
  const cannotBeDisabled = enabledByDefault && supportValueInUrl;
  const isChangedFeature = enabledByDefault !== isFeatureEnabled;

  const featureValue = useAppSelector(selectGetFeatureValueIfEnabled(name));
  const [textField, setTextField] = useState(() => {
    if (typeof featureValue === "string") {
      return featureValue;
    }
  });

  const areButtonsDisabled =
    (!canBeUserEnabled && requiresReload) || cannotBeDisabled;

  const toggleFeature = useCallback(() => {
    const url = new URL(
      window.location.origin +
        window.location.pathname +
        window.location.search,
    );

    // Check if the feature flag is already in the url
    const featureKeyInUrl = [...url.searchParams.keys()].find(
      (key) => key.toLowerCase() === name.toLowerCase(),
    );
    // Remove the feature flag from the url
    if (featureKeyInUrl) {
      url.searchParams.delete(featureKeyInUrl);
    }

    if (isFeatureEnabled) {
      dispatch(disableFeature(name));

      // The feature flag was already removed, but if the feature is enabled by default it needs to be set to false explicitly
      if (enabledByDefault && !cannotBeDisabled) {
        url.searchParams.set(name, "false");
      }
    } else {
      dispatch(enableFeature({ featureFlag: name, value: textField ?? null }));

      // Add feature flag to the URL if needed
      if (!enabledByDefault && canBeUserEnabled) {
        url.searchParams.set(name, textField ?? "");
      }
    }

    if (requiresReload) {
      // Refresh the page
      window.location.href = url.toString();
    } else {
      // Update the URL without reloading the page
      window.history.replaceState(window.history.state, "", url.toString());
    }
  }, [
    name,
    isFeatureEnabled,
    cannotBeDisabled,
    enabledByDefault,
    canBeUserEnabled,
    requiresReload,
    textField,
    dispatch,
  ]);

  return (
    <TableRow>
      <TableCell align="left" sx={{ p: 1 }}>
        <FaroText variant="heading12">{name}</FaroText>
      </TableCell>
      <TableCell align="left" sx={{ p: 1 }}>
        <FaroText variant="helpText">{description}</FaroText>
      </TableCell>
      <TableCell align="left" sx={{ p: 1 }}>
        <ChangeIndicatorBadge invisible={!isChangedFeature}>
          <FaroSwitch
            disabled={areButtonsDisabled}
            checked={isFeatureEnabled}
            onToggle={toggleFeature}
          />
        </ChangeIndicatorBadge>
      </TableCell>
      <TableCell align="left" sx={{ p: 1 }}>
        {supportValueInUrl && (
          <TextField
            disabled={areButtonsDisabled || isFeatureEnabled}
            text={textField}
            onTextChanged={setTextField}
            fullWidth
            variant="underlined"
          />
        )}
      </TableCell>
      <TableCell align="left" sx={{ p: 0 }}>
        <FaroText variant="helpText" sx={{ width: "100%" }}>
          {convertToDateString(createdAt)}
        </FaroText>
      </TableCell>
    </TableRow>
  );
}
